import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import { listAdkSessions, listActiveAdkSessions, renameAdkSession, deleteAdkSession, setSessionPinned, setSessionArchived, cancelAdkSession, parseVfsSessionId, generateAdkSessionLabel } from '../services/AdkSessionsService';
import { findProjectByConversationId, getProjectAccess } from '../utils/vfsClient';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import { useAuth } from '../context/AuthContext';
import miaFlowAuth from '../auth/MiaFlowAuth';
import Icon from '../components/icons/Icon';
import ConfirmDialog from '../components/ConfirmDialog';
import SessionKanbanBoard from '../components/SessionKanbanBoard';
import SessionStepsDrawer from '../components/SessionStepsDrawer';
import Activity from './Activity';
import { Segmented } from 'antd';
import config from '../config';
import { getMyActivity } from '../services/SandboxService';
import ToolSessionCard, { splitToolSessions, toolSessionKey } from '../components/sandbox/ToolSessionCard';
import { projectHomeUrl } from '../utils/openProject';
import KanbanLiveBar from '../components/workspace/KanbanLiveBar';
import LiveAgentCard from '../components/workspace/LiveAgentCard';
import useLiveNow from '../hooks/useLiveNow';
import useSessionDrift from '../hooks/useSessionDrift';
import { driftText } from '../utils/driftModel';
import { SESSION_FILTERS, chatsForFilter, liveAgentCards } from '../utils/sessionsModel';
import { liveSummary } from '../utils/liveModel';
import { followPerson } from '../utils/presenceClient';
import './Memories.css';

// Persisted so re-opening History keeps whichever view was last used,
// same convention as the playbook-choice/resolved-playbook localStorage
// keys in VfsChatPanel.jsx.
const VIEW_MODE_KEY = 'mia_flow_history_view_mode';
// The Chats / Projects filter (sandbox-first), remembered the same way.
const SOURCE_KEY = 'mia_flow_history_source';
const VALID_SOURCES = ['chats', 'projects'];

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const formatDate = (epochSeconds) => {
  if (!epochSeconds) return '';
  const date = new Date(epochSeconds * 1000);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return diffMins === 1 ? '1 minute ago' : `${diffMins} minutes ago`;
  if (diffHours < 24) return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffWeeks === 1) return 'Last week';
  if (diffWeeks < 4) return `${diffWeeks} weeks ago`;
  if (diffMonths === 1) return 'Last month';
  if (diffMonths < 12) return `${diffMonths} months ago`;
  if (diffYears === 1) return 'Last year';
  return `${diffYears} years ago`;
};

function Memories() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const userId = user?.sub || user?.id || user?.email;

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Live "currently running" state — same `/active` endpoint and polling
  // cadence as BackgroundTasksIndicator.jsx (this user's own live sidebar
  // indicator), reused here rather than re-invented so a session started as
  // a parallel/fork spawn (tracked in the same active_runs registry) shows
  // up here too, the moment it exists as a real session id.
  const [activeIds, setActiveIds] = useState(() => new Set());
  // The first active-runs poll answered (or failed): until then the Kanban can't tell Active from Recent.
  const [activeIdsLoaded, setActiveIdsLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Bulk selection — a separate concern from the per-row inline
  // rename/delete affordances above: selecting a conversation for bulk
  // action doesn't enter its rename/delete state, and vice versa (see the
  // `editingId`/`deletingId` guards in the checkbox and row handlers below).
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Pin/archive are reversible one-click toggles (same as the per-row
  // star/archive buttons), so — unlike delete — bulk pin/archive run
  // immediately with no confirmation dialog.
  const [bulkPinning, setBulkPinning] = useState(false);
  const [bulkArchiving, setBulkArchiving] = useState(false);

  // Month-group collapse state — see the effect below that seeds it once.
  const [collapsedMonths, setCollapsedMonths] = useState(() => new Set());
  const collapsedMonthsInitialized = useRef(false);

  // Kanban / List / Activity view switch — Kanban (task #193) and Activity
  // (task #212) used to be their own pages/nav items, but now live here as
  // a 3-way toggle, Kanban first (the default) since it's the more common
  // "what's running / what needs filing" view, reusing the same
  // `sessions`/`activeIds` state already loaded/polled on this page (see
  // SessionKanbanBoard) instead of a duplicate fetch. Selection mode /
  // bulk-delete are list-only concerns and are hidden outside List view.
  // `?view=kanban`/`?view=activity` (App.jsx's old /kanban and /activity ->
  // /history redirects land here) take priority over the persisted choice
  // — an explicit link should win over whatever was last used.
  const VALID_VIEW_MODES = ['kanban', 'list', 'activity'];
  const [viewMode, setViewMode] = useState(() => {
    const requested = searchParams.get('view');
    if (VALID_VIEW_MODES.includes(requested)) return requested;
    try {
      const stored = localStorage.getItem(VIEW_MODE_KEY);
      return VALID_VIEW_MODES.includes(stored) ? stored : 'kanban';
    } catch {
      return 'kanban';
    }
  });
  const [movingId, setMovingId] = useState(null);
  // List view hides archived sessions by default (see `displayed` below) —
  // this reveals them on request instead of them just vanishing with no way
  // back short of switching to the Kanban view's Archived column.
  const [showArchived, setShowArchived] = useState(false);
  // Kanban card click opens this session's step-detail drawer instead of
  // navigating away immediately — see SessionStepsDrawer.
  const [stepsSession, setStepsSession] = useState(null);

  const setViewModePersisted = (mode) => {
    setViewMode(mode);
    if (mode !== 'list') {
      // Selection/bulk-delete is a list-only concern (Kanban's own card
      // actions cover pin/archive per-card already; Activity has none).
      setSelectionMode(false);
      setSelectedIds(new Set());
    }
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      // best-effort persistence only
    }
  };

  // Sandbox-first (docs/SANDBOX_FIRST.md): what the board shows — Chats (the Flow conversations, with the Kanban /
  // List / Activity views) or Projects (the project cards: the sessions of the person's AI tools on their sandbox,
  // vfs-service /api/v1/me/activity, each a project's changes). Projects always shows the Kanban, where they sit in
  // Active (last call within ten minutes) and Recent. Chats by default; the choice is remembered per browser.
  const [source, setSource] = useState(() => {
    try {
      const stored = localStorage.getItem(SOURCE_KEY);
      return VALID_SOURCES.includes(stored) ? stored : 'chats';
    } catch {
      return 'chats';
    }
  });
  const setSourcePersisted = (next) => {
    setSource(next);
    setProjectNotice(null);
    try {
      localStorage.setItem(SOURCE_KEY, next);
    } catch {
      // best-effort persistence only
    }
  };
  // Collaboration (design board "Sessions kanban"): whose sessions the board shows — Mine, Everyone (also the AI agents
  // at work in your shared projects, from the page's /api/v1/live poll) or AI only (what an agent runs right now) —
  // remembered in this browser.
  const [whose, setWhoseState] = useState(() => {
    try {
      const saved = localStorage.getItem('flow.sessions.whose');
      return SESSION_FILTERS.some((f) => f.value === saved) ? saved : 'mine';
    } catch { return 'mine'; }
  });
  const setWhose = (next) => {
    setWhoseState(next);
    try { localStorage.setItem('flow.sessions.whose', next); } catch { /* not remembered */ }
  };
  const liveAnswer = useLiveNow();
  const [toolSessions, setToolSessions] = useState([]);
  // Where the person filed each AI tool session on the board ({ [toolSessionKey]: 'pinned' | 'archived' }): a
  // per-browser preference, like the Kanban columns of chats are per person.
  const toolPlacementKey = `flow.activity.toolPlacement.${userId || 'anonymous'}`;
  const [toolPlacement, setToolPlacement] = useState(() => {
    try { return JSON.parse(localStorage.getItem(toolPlacementKey) || '{}') || {}; } catch (_error) { return {}; }
  });
  const moveToolSession = (key, target) => {
    setToolPlacement((prev) => {
      const next = {...prev};
      if (target === 'pinned' || target === 'archived') next[key] = target;
      else delete next[key];
      try { localStorage.setItem(toolPlacementKey, JSON.stringify(next)); } catch (_error) { /* not remembered */ }
      return next;
    });
  };
  const [toolsUnavailable, setToolsUnavailable] = useState(false);
  // First answer from the AI tools' activity still pending (the Kanban shows skeletons, not "Nothing running").
  const [toolsLoading, setToolsLoading] = useState(Boolean(config.sandboxFirst));
  useEffect(() => {
    if (!config.sandboxFirst) return undefined;
    let cancelled = false;
    const load = () => getMyActivity({ limit: 50 })
      .then((activity) => {
        if (cancelled) return;
        setToolsUnavailable(activity === null);
        setToolSessions(activity?.sessions || []);
      })
      .catch(() => {
        // Best effort: keep what was shown.
      })
      .finally(() => {
        if (!cancelled) setToolsLoading(false);
      });
    load();
    const interval = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);
  const showTools = config.sandboxFirst && source === 'projects';
  const effectiveView = showTools ? 'kanban' : viewMode;
  const openToolSession = (session) => {
    const tenant = miaFlowAuth.getSelectedTenant();
    const tenantId = tenant?.tenantId || tenant?.id;
    navigate(projectHomeUrl(tenantId, session.projectId).replace('view=dashboard', 'view=files'));
  };

  const loadSessions = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const results = await listAdkSessions(userId);
      const sorted = [...results].sort((a, b) => (b.lastUpdateTime || 0) - (a.lastUpdateTime || 0));
      setSessions(sorted);
    } catch (err) {
      console.error('Failed to load ADK sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Poll GET /api/sessions/{app}/{user}/active for the live WIP badge — a
  // spawned parallel/fork child session lands in this same set as soon as
  // adk-be-app's active_runs registry knows about it, even before a full
  // `loadSessions()` refresh has picked up its row (the badge then simply
  // has nothing to attach to until the row appears).
  const POLL_INTERVAL_MS = 4000;
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const ids = await listActiveAdkSessions(userId);
        if (!cancelled) { setActiveIds(new Set(ids)); }
      } catch {
        // Best-effort live badge — a transient failure just skips this tick.
      } finally {
        if (!cancelled) { setActiveIdsLoaded(true); }
      }
    };
    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [userId]);

  // Backfill "Untitled Conversation" rows: adk-be-app's automatic naming
  // (session_namer.py, triggered on the first turn) only gets a ~24s
  // window on the LIVE chat session (mia-chatbot-widget retries the label
  // fetch 3 times, then gives up forever — even on a later revisit, see
  // hydrateSession's labelFetchDoneRef). Any session whose first attempt
  // missed that window (LLM latency, a transient error) is stuck with no
  // name permanently, with no way to retry short of this page explicitly
  // calling the same POST .../label/generate endpoint the live chat used —
  // it's idempotent (never overwrites a real label) so this is safe to run
  // every time the list loads. Bounded concurrency so a page with many
  // unlabeled sessions doesn't fire dozens of naming calls at once.
  const labelBackfillInFlightRef = useRef(new Set());
  // Asked once per session per page visit, whatever the outcome; a session the backend says cannot be named (422: no
  // turns to name it from; 404: gone) is remembered across visits too, so it is never asked again.
  const labelBackfillTriedRef = useRef(new Set());
  useEffect(() => {
    if (!userId) return;
    const skipped = labelBackfillSkipped();
    const unlabeled = sessions.filter(
      (s) => !s.name && !labelBackfillInFlightRef.current.has(s.id) && !labelBackfillTriedRef.current.has(s.id) && !skipped.has(s.id)
    );
    if (unlabeled.length === 0) return;

    const CONCURRENCY = 3;
    let cursor = 0;
    const runNext = async () => {
      if (cursor >= unlabeled.length) return;
      const session = unlabeled[cursor];
      cursor += 1;
      labelBackfillInFlightRef.current.add(session.id);
      labelBackfillTriedRef.current.add(session.id);
      try {
        const name = await generateAdkSessionLabel(session.appName, userId, session.id);
        if (name) {
          setSessions((prev) => prev.map((s) => (s.id === session.id ? {...s, name} : s)));
        }
      } catch (err) {
        if (err?.status === 422 || err?.status === 404) {
          skipLabelBackfill(session.id);
          return;
        }
        // Best-effort backfill — a failure here just leaves the row as
        // "Untitled Conversation" for now, no user-facing error needed.
        console.debug('Session label backfill failed:', session.id, err);
      } finally {
        labelBackfillInFlightRef.current.delete(session.id);
        await runNext();
      }
    };
    const workers = Array.from({length: Math.min(CONCURRENCY, unlabeled.length)}, runNext);
    Promise.all(workers).catch(() => {});
    // Intentionally not re-running on every `sessions` update (would refire
    // on every rename/pin/etc.) — only when the userId or the loaded set of
    // session ids actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, sessions.map((s) => s.id).join(',')]);

  const handleSelectConversation = async (session) => {
    if (editingId) return;
    const parsed = parseVfsSessionId(session.id);
    // A vfs-tied session id (parseVfsSessionId returns a real projectId) means
    // this conversation actually created/wrote to a real project via
    // vfs_create_project/vfs_write_file — land on Code so the result is
    // visible. A session with no vfs association was chat-only (no code
    // produced) — land on the plain Chat view instead of bare /code, which
    // would otherwise silently auto-create a brand-new, unwanted project
    // (see App.jsx's URL-validation effect / task #79's silent-create fix).
    // parseVfsSessionId only decodes the OLD deterministic `vfs-{projectId}`
    // id shape — a real ADK-issued session id (a plain UUID, the normal case
    // for any conversation that started on the plain /chat door) never
    // matches it, so fall back to the conversationId -> projectId reverse
    // lookup (same one VfsChatPanel/Home.jsx use) before assuming chat-only.
    const tenantId = miaFlowAuth.getSelectedTenant()?.tenantId || miaFlowAuth.getSelectedTenant()?.id;
    let projectId = parsed?.projectId;
    if (!projectId && tenantId) {
      projectId = await findProjectByConversationId(session.id);
    }
    if (projectId && tenantId) {
      navigate(`/code/${tenantId}/${projectId}?conversationId=${encodeURIComponent(session.id)}`);
    } else {
      navigate(`/chat?conversationId=${encodeURIComponent(session.id)}`);
    }
  };

  const handleNewChat = () => {
    navigate('/chat');
  };

  const handleEditClick = (e, session) => {
    e.stopPropagation();
    setDeletingId(null);
    setEditingId(session.id);
    setEditingName(session.name || '');
  };

  const handleEditCancel = (e) => {
    e && e.stopPropagation();
    setEditingId(null);
    setEditingName('');
  };

  const handleEditConfirm = async (e) => {
    e && e.stopPropagation();
    const name = editingName.trim();
    if (!name || !editingId) { handleEditCancel(); return; }
    setSavingId(editingId);
    try {
      const appName = sessions.find(s => s.id === editingId)?.appName;
      await renameAdkSession(appName, userId, editingId, name);
      setSessions(prev => prev.map(s => s.id === editingId ? {...s, name} : s));
    } catch (err) {
      console.error('Failed to rename session:', err);
    }
    setSavingId(null);
    setEditingId(null);
    setEditingName('');
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') handleEditConfirm(e);
    if (e.key === 'Escape') handleEditCancel(e);
  };

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setEditingId(null);
    setEditingName('');
    setDeletingId(id);
  };

  const handleDeleteCancel = (e) => {
    e && e.stopPropagation();
    setDeletingId(null);
  };

  const handleDeleteConfirm = async (e, id) => {
    e && e.stopPropagation();
    try {
      const appName = sessions.find(s => s.id === id)?.appName;
      await deleteAdkSession(appName, userId, id);
      setSessions(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
    setDeletingId(null);
  };

  const handleTogglePin = async (e, session) => {
    e.stopPropagation();
    const nextPinned = !session.state?.pinned;
    // Optimistic — pin/archive is a plain state-merge PATCH (see
    // AdkSessionsService's own doc on why: this backend has no dedicated
    // `/pin` route), fast enough that waiting for the round-trip before
    // updating the UI would read as sluggish for a one-click toggle.
    setSessions(prev => prev.map(s => s.id === session.id
      ? {...s, state: {...s.state, pinned: nextPinned}}
      : s));
    try {
      await setSessionPinned(session.appName, userId, session.id, nextPinned);
    } catch (err) {
      console.error('Failed to toggle pin:', err);
      setSessions(prev => prev.map(s => s.id === session.id
        ? {...s, state: {...s.state, pinned: !nextPinned}}
        : s));
    }
  };

  const handleArchive = async (e, session) => {
    e.stopPropagation();
    const nextArchived = !session.state?.archived;
    // Archiving drops it out of `displayed` unless "Show archived" is on
    // (see that filter above) — optimistic update either way, restored on
    // failure.
    setSessions(prev => prev.map(s => s.id === session.id
      ? {...s, state: {...s.state, archived: nextArchived}}
      : s));
    try {
      await setSessionArchived(session.appName, userId, session.id, nextArchived);
    } catch (err) {
      console.error('Failed to toggle archive:', err);
      setSessions(prev => prev.map(s => s.id === session.id
        ? {...s, state: {...s.state, archived: !nextArchived}}
        : s));
    }
  };

  // Kanban-view card move — same pinned/archived flag-flip as the list
  // view's star/archive buttons (handleTogglePin/handleArchive above),
  // just addressed by target column instead of a single toggle. Mirrors
  // pages/Kanban.jsx's own `moveTo` exactly.
  const moveTo = async (session, column) => {
    setMovingId(session.id);
    const prevState = session.state || {};
    const nextState = column === 'pinned'
      ? { ...prevState, pinned: true, archived: false }
      : column === 'archived'
        ? { ...prevState, pinned: false, archived: true }
        : { ...prevState, pinned: false, archived: false };
    setSessions(prev => prev.map(s => s.id === session.id ? {...s, state: nextState} : s));
    try {
      if (column === 'pinned') {
        await setSessionPinned(session.appName, userId, session.id, true);
        if (prevState.archived) await setSessionArchived(session.appName, userId, session.id, false);
      } else if (column === 'archived') {
        await setSessionArchived(session.appName, userId, session.id, true);
        if (prevState.pinned) await setSessionPinned(session.appName, userId, session.id, false);
      } else {
        if (prevState.pinned) await setSessionPinned(session.appName, userId, session.id, false);
        if (prevState.archived) await setSessionArchived(session.appName, userId, session.id, false);
      }
    } catch (err) {
      console.error('Failed to move session:', err);
      setSessions(prev => prev.map(s => s.id === session.id ? {...s, state: prevState} : s));
    } finally {
      setMovingId(null);
    }
  };

  // Kanban "Stop" — dragging a card out of Active, its per-card Stop
  // button, or the Active column's own bulk action all land here. Removes
  // the id from `activeIds` optimistically (the next 4s poll would do it
  // anyway, but a manual stop should feel instant, not wait out the poll
  // interval) — safe even if the cancel call turns out to 404 (the run had
  // already finished on its own), since that's a success case too (see
  // cancelAdkSession's own doc).
  const handleStopSession = async (session) => {
    setActiveIds(prev => {
      if (!prev.has(session.id)) return prev;
      const next = new Set(prev);
      next.delete(session.id);
      return next;
    });
    try {
      await cancelAdkSession(session.appName, userId, session.id);
    } catch (err) {
      console.error('Failed to stop session:', err);
      // Best-effort — if the cancel call genuinely failed (not just a 404
      // "already finished"), the next active-runs poll corrects the
      // optimistic removal above by re-adding it if it's still really running.
    }
  };

  // Kanban's Archive-column bulk delete — a separate confirm/in-flight
  // state from the List view's own bulk delete above (bulkDeleteConfirmOpen/
  // bulkDeleting/selectedIds), since the two selections are independent
  // (list-view rows vs. one Kanban column's selected cards) and mixing them
  // would either delete the wrong set or leave one UI's "N selected" count
  // out of sync with the other's.
  const [kanbanDeleteIds, setKanbanDeleteIds] = useState(null);

  // Projects selected for deletion in the Kanban's Archived column:
  // `{ owned: [{ projectId, name }], notOwned: [name] }` while the
  // confirmation is open. Only the owner can delete a project (vfs-service
  // sharing roles, docs/project-sharing.md), so ownership is checked first
  // with the same access call the Share dialog uses, and the projects the
  // person doesn't own are skipped and named.
  const [projectDelete, setProjectDelete] = useState(null);
  const [projectNotice, setProjectNotice] = useState(null);

  const requestProjectDelete = async (items) => {
    const byProject = new Map();
    items.forEach((item) => {
      if (item?.projectId && !byProject.has(item.projectId)) {
        byProject.set(item.projectId, {projectId: item.projectId, name: item.projectName || item.projectId});
      }
    });
    const projects = Array.from(byProject.values());
    if (projects.length === 0) return;
    setProjectNotice(null);
    const access = await Promise.allSettled(projects.map((project) => getProjectAccess(project.projectId)));
    const owned = projects.filter((_project, i) => access[i].status === 'fulfilled' && access[i].value?.role === 'owner');
    const notOwned = projects.filter((project) => !owned.includes(project)).map((project) => project.name);
    if (owned.length === 0) {
      setProjectNotice(`Only the owner can delete a project. You don't own ${notOwned.join(', ')}, so nothing was deleted.`);
      return;
    }
    setProjectDelete({owned, notOwned});
  };

  // The board's Archived-column Delete: chat ids go to the conversations'
  // confirmation, selected project cards to the projects' one.
  const handleKanbanBulkDelete = (ids, extras = []) => {
    if (ids.length > 0) setKanbanDeleteIds(ids);
    if (extras.length > 0) requestProjectDelete(extras.map((extra) => extra.item));
  };

  // Same call and pacing as the Projects page's bulk delete (Projects.jsx).
  const handleProjectDeleteConfirm = async () => {
    if (!projectDelete) return;
    const {owned, notOwned} = projectDelete;
    setProjectDelete(null);
    const CONCURRENCY = 5;
    const results = [];
    for (let i = 0; i < owned.length; i += CONCURRENCY) {
      const batch = owned.slice(i, i + CONCURRENCY);
      // eslint-disable-next-line no-await-in-loop
      results.push(...await Promise.allSettled(batch.map((project) => miaPlatformDataService.deleteProject(project.projectId))));
    }
    const deleted = new Set(owned.filter((_project, i) => results[i].status === 'fulfilled').map((project) => project.projectId));
    const failed = owned.length - deleted.size;
    setToolSessions((prev) => prev.filter((session) => !deleted.has(session.projectId)));
    const parts = [];
    if (deleted.size > 0) parts.push(`Deleted ${plural(deleted.size, 'project')}.`);
    if (failed > 0) parts.push(`${plural(failed, 'project')} could not be deleted.`);
    if (notOwned.length > 0) parts.push(`Skipped ${notOwned.join(', ')}: only the owner can delete a project.`);
    setProjectNotice(parts.join(' '));
  };

  const handleKanbanBulkDeleteConfirm = async () => {
    if (!kanbanDeleteIds) return;
    const ids = kanbanDeleteIds;
    const failed = [];
    for (const id of ids) {
      try {
        const appName = sessions.find(s => s.id === id)?.appName;
        // eslint-disable-next-line no-await-in-loop
        await deleteAdkSession(appName, userId, id);
      } catch (err) {
        console.error(`Failed to delete session ${id}:`, err);
        failed.push(id);
      }
    }
    setSessions(prev => prev.filter(s => !ids.includes(s.id) || failed.includes(s.id)));
    setKanbanDeleteIds(null);
  };

  const toggleSelectionMode = () => {
    setSelectionMode(prev => {
      if (prev) setSelectedIds(new Set());
      return !prev;
    });
  };

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllVisible = () => {
    setSelectedIds(prev => {
      const allVisible = displayed.every(s => prev.has(s.id));
      if (allVisible) return new Set();
      return new Set(displayed.map(s => s.id));
    });
  };

  const handleBulkDeleteConfirm = async () => {
    setBulkDeleting(true);
    const ids = Array.from(selectedIds);
    const failed = [];
    for (const id of ids) {
      try {
        const appName = sessions.find(s => s.id === id)?.appName;
        // eslint-disable-next-line no-await-in-loop
        await deleteAdkSession(appName, userId, id);
      } catch (err) {
        console.error(`Failed to delete session ${id}:`, err);
        failed.push(id);
      }
    }
    setSessions(prev => prev.filter(s => !ids.includes(s.id) || failed.includes(s.id)));
    setSelectedIds(new Set(failed));
    setBulkDeleting(false);
    setBulkDeleteConfirmOpen(false);
    if (failed.length === 0) setSelectionMode(false);
  };

  const handleBulkPin = async () => {
    setBulkPinning(true);
    const targets = sessions.filter(s => selectedIds.has(s.id));
    const failed = [];
    for (const session of targets) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await setSessionPinned(session.appName, userId, session.id, true);
      } catch (err) {
        console.error(`Failed to pin session ${session.id}:`, err);
        failed.push(session.id);
      }
    }
    const succeededIds = new Set(targets.filter(s => !failed.includes(s.id)).map(s => s.id));
    setSessions(prev => prev.map(s => succeededIds.has(s.id)
      ? {...s, state: {...s.state, pinned: true, archived: false}}
      : s));
    setSelectedIds(new Set(failed));
    setBulkPinning(false);
    if (failed.length === 0) setSelectionMode(false);
  };

  const handleBulkArchive = async () => {
    setBulkArchiving(true);
    const targets = sessions.filter(s => selectedIds.has(s.id));
    const failed = [];
    for (const session of targets) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await setSessionArchived(session.appName, userId, session.id, true);
      } catch (err) {
        console.error(`Failed to archive session ${session.id}:`, err);
        failed.push(session.id);
      }
    }
    const succeededIds = new Set(targets.filter(s => !failed.includes(s.id)).map(s => s.id));
    // Archived sessions drop out of `displayed` (see its own `!archived`
    // filter below), same as the per-row archive action.
    setSessions(prev => prev.map(s => succeededIds.has(s.id)
      ? {...s, state: {...s.state, pinned: false, archived: true}}
      : s));
    setSelectedIds(new Set(failed));
    setBulkArchiving(false);
    if (failed.length === 0) setSelectionMode(false);
  };

  const displayed = (searchQuery.trim()
    ? sessions.filter(session =>
        (session.name || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sessions
  )
    .filter(session => showArchived || !session.state?.archived)
    // Pinned first (loadSessions already sorted by lastUpdateTime desc within
    // that), same convention as the reference "keep on top" behavior.
    .sort((a, b) => (b.state?.pinned ? 1 : 0) - (a.state?.pinned ? 1 : 0));

  const archivedCount = sessions.filter(s => s.state?.archived).length;

  // Kanban view's own source list — unlike `displayed` above, archived
  // sessions stay in (Kanban has a real Archived column to put them in)
  // and there's no pinned-first sort (SessionKanbanBoard buckets by column
  // itself). Search still applies so the two views stay consistent.
  const kanbanSessions = showTools ? [] : chatsForFilter(searchQuery.trim()
    ? sessions.filter(session => (session.name || '').toLowerCase().includes(searchQuery.toLowerCase()))
    : sessions, activeIds, whose);

  // Others' AI agents at work in your shared projects (Everyone, AI only): Active cards; "Watch" opens the project
  // following the agent's driver.
  const joinProject = (projectId) => navigate(`/open/${encodeURIComponent(projectId)}`);
  const liveCards = whose === 'mine' ? [] : liveSummary(liveAnswer).projects.flatMap((project) => liveAgentCards(
    { you: userId ? { id: userId } : null, agents: project.agents },
    { projectName: project.name || project.projectId },
  ).map((card) => ({
    key: `${project.projectId}:${card.key}`,
    node: <LiveAgentCard card={card} onWatch={() => { if (card.agent.driver?.id) { followPerson(project.projectId, card.agent.driver.id); } joinProject(project.projectId); }} />,
  })));
  // A recent chat whose files someone else changed since: said on its card (hooks/useSessionDrift).
  const projectOfSession = useCallback((session) => parseVfsSessionId(session.id)?.projectId || null, []);
  const drifts = useSessionDrift(showTools ? [] : sessions, activeIds, projectOfSession);
  const cardNotes = Object.fromEntries(Object.entries(drifts).map(([id, drift]) => [id, (
    <div className="session-drift" data-testid="session-drift" key={id} title={drift.files.join('\n')}>{driftText(drift)}</div>
  )]));

  // Sandbox-first: the AI tool sessions as extra Kanban cards (same search, on the project's name).
  const toolCards = (() => {
    if (!showTools) return null;
    const query = searchQuery.trim().toLowerCase();
    const matching = query
      ? toolSessions.filter(session => `${session.projectName || session.projectId} ${session.via || ''}`.toLowerCase().includes(query))
      : toolSessions;
    const {active, recent} = splitToolSessions(matching);
    const toCard = (session, movable) => {
      const key = toolSessionKey(session);
      const node = <ToolSessionCard onOpen={openToolSession} session={session} />;
      // A filed or recent project card can be selected (select-all included) and moved or deleted in bulk.
      return movable
        ? {key, node, onMove: (target) => moveToolSession(key, target), selectable: true, label: session.projectName || session.projectId, item: session}
        : {key, node};
    };
    // A running session stays in Active; the others go where the person dragged them (Recent by default).
    const placed = {recent: [], pinned: [], archived: []};
    recent.forEach((session) => { placed[toolPlacement[toolSessionKey(session)] || 'recent'].push(toCard(session, true)); });
    return {active: active.map((session) => toCard(session, false)), ...placed};
  })();
  const boardExtraCards = liveCards.length > 0
    ? {...(toolCards || {}), active: [...liveCards, ...((toolCards && toolCards.active) || [])]}
    : toolCards;

  // Group by calendar month, newest first. Only the most recent group
  // (this month, or the newest month with any activity) starts expanded;
  // every older month starts collapsed — `collapsedMonthsInitialized` guards
  // the one-time auto-collapse so a user's later manual expand/collapse
  // isn't undone by a re-render (search, a rename, etc.).
  const monthKey = (epochSeconds) => {
    if (!epochSeconds) return 'unknown';
    const d = new Date(epochSeconds * 1000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const monthLabel = (key) => {
    if (key === 'unknown') return 'Unknown';
    const [year, month] = key.split('-').map(Number);
    return new Date(year, month - 1, 1).toLocaleDateString(undefined, {month: 'long', year: 'numeric'});
  };
  const groups = [];
  const groupIndex = new Map();
  for (const session of displayed) {
    const key = monthKey(session.lastUpdateTime);
    if (!groupIndex.has(key)) {
      groupIndex.set(key, groups.length);
      groups.push({key, label: monthLabel(key), sessions: []});
    }
    groups[groupIndex.get(key)].sessions.push(session);
  }

  useEffect(() => {
    if (collapsedMonthsInitialized.current || groups.length === 0) return;
    collapsedMonthsInitialized.current = true;
    setCollapsedMonths(new Set(groups.slice(1).map(g => g.key)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups.length]);

  const toggleMonthCollapsed = (key) => {
    setCollapsedMonths(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="memories-page">
      <div className="memories-header">
        <div className="memories-title-row">
          <Icon name="clock" size={22} />
          <h1 className="memories-title">Activity</h1>
        </div>
        <p className="memories-subtitle">
          {config.sandboxFirst
            ? 'What happened in your sandbox: sessions from your AI tools and, with Chat on, your conversations.'
            : 'Your chat history — browse, rename or continue past conversations.'}
        </p>
      </div>

      <div className="memories-toolbar">
        <div className="memories-search-wrapper">
          <Icon name="search" size={16} />
          <input
            className="memories-search-input"
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations..."
            type="text"
            value={searchQuery}
          />
          {searchQuery && (
            <button
              className="memories-search-clear"
              onClick={() => setSearchQuery('')}
              title="Clear search"
              type="button"
            >
              <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                <line x1="18" x2="6" y1="6" y2="18"/>
                <line x1="6" x2="18" y1="6" y2="18"/>
              </svg>
            </button>
          )}
        </div>
        {config.sandboxFirst && (
          <Segmented
            aria-label="Source"
            onChange={setSourcePersisted}
            options={[{value: 'chats', label: 'Chats'}, {value: 'projects', label: 'Projects'}]}
            value={source}
          />
        )}
        {effectiveView === 'kanban' && (
          <div aria-label="Whose sessions" className="sessions-whose" role="group">
            {SESSION_FILTERS.map((filter) => (
              <button
                aria-pressed={whose === filter.value}
                className={`workspace-subtab${whose === filter.value ? ' is-on' : ''}`}
                data-testid={`sessions-whose-${filter.value}`}
                key={filter.value}
                onClick={() => setWhose(filter.value)}
                type="button"
              >
                {filter.label}
              </button>
            ))}
          </div>
        )}
        {!showTools && <div className="memories-view-switch" role="group">
          <button
            className={`memories-view-switch-btn${viewMode === 'kanban' ? ' active' : ''}`}
            onClick={() => setViewModePersisted('kanban')}
            title="Kanban view"
            aria-label="Kanban view"
            type="button"
          >
            <Icon name="template" size={14} />
          </button>
          <button
            className={`memories-view-switch-btn${viewMode === 'list' ? ' active' : ''}`}
            onClick={() => setViewModePersisted('list')}
            title="List view"
            aria-label="List view"
            type="button"
          >
            <Icon name="list" size={14} />
          </button>
          <button
            className={`memories-view-switch-btn${viewMode === 'activity' ? ' active' : ''}`}
            onClick={() => setViewModePersisted('activity')}
            title="Activity view"
            aria-label="Activity view"
            type="button"
          >
            <Icon name="bar-chart" size={14} />
          </button>
        </div>}
        {effectiveView === 'list' && archivedCount > 0 && (
          <button
            className={`memories-select-btn${showArchived ? ' active' : ''}`}
            onClick={() => setShowArchived(v => !v)}
            title={showArchived ? 'Hide archived conversations' : 'Show archived conversations'}
            type="button"
          >
            <Icon name="package" size={13} />
            {showArchived ? 'Hide archived' : `Archived (${archivedCount})`}
          </button>
        )}
        {effectiveView === 'list' && (
          <button
            className={`memories-select-btn${selectionMode ? ' active' : ''}`}
            onClick={toggleSelectionMode}
            type="button"
          >
            {selectionMode ? 'Cancel' : 'Select'}
          </button>
        )}
        <button className="memories-new-btn" onClick={handleNewChat} type="button">
          <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
            <line x1="12" x2="12" y1="5" y2="19"/>
            <line x1="5" x2="19" y1="12" y2="12"/>
          </svg>
          New chat
        </button>
      </div>

      {selectionMode && effectiveView === 'list' && (
        <div className="memories-bulk-bar">
          <label className="memories-bulk-select-all">
            <input
              checked={displayed.length > 0 && displayed.every(s => selectedIds.has(s.id))}
              onChange={handleSelectAllVisible}
              type="checkbox"
            />
            <span>{selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}</span>
          </label>
          <button
            className="memories-bulk-action-btn"
            disabled={selectedIds.size === 0 || bulkPinning || bulkArchiving || bulkDeleting}
            onClick={handleBulkPin}
            type="button"
          >
            <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
              <path d="M12 2v8l4.5 3.6a1 1 0 01-.6 1.8H8.1a1 1 0 01-.6-1.8L12 10"/>
              <path d="M12 15v7"/>
            </svg>
            Pin{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </button>
          <button
            className="memories-bulk-action-btn"
            disabled={selectedIds.size === 0 || bulkPinning || bulkArchiving || bulkDeleting}
            onClick={handleBulkArchive}
            type="button"
          >
            <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
              <rect height="5" rx="1" width="20" x="2" y="4"/>
              <path d="M4 9v9a2 2 0 002 2h12a2 2 0 002-2V9"/>
              <path d="M10 13h4"/>
            </svg>
            Archive{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </button>
          <button
            className="memories-bulk-delete-btn"
            disabled={selectedIds.size === 0 || bulkPinning || bulkArchiving || bulkDeleting}
            onClick={() => setBulkDeleteConfirmOpen(true)}
            type="button"
          >
            <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
              <path d="M10 11v6"/>
              <path d="M14 11v6"/>
              <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
            </svg>
            Delete{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </button>
        </div>
      )}

      {showTools && toolsUnavailable && (
        <p className="memories-subtitle" role="status">The sessions of your AI tools need a newer sandbox.</p>
      )}
      {showTools && projectNotice && (
        <p className="memories-subtitle" role="status">{projectNotice}</p>
      )}

      <div className={`memories-content${effectiveView === 'kanban' ? ' memories-content-kanban' : ''}${effectiveView === 'activity' ? ' memories-content-activity' : ''}`}>
        {effectiveView === 'activity' ? (
          <Activity embedded />
        ) : effectiveView === 'kanban' ? (
          <>
          <KanbanLiveBar onJoin={joinProject} you={userId ? { id: userId } : null} />
          <SessionKanbanBoard
            activeIds={activeIds}
            cardNotes={cardNotes}
            extraCards={boardExtraCards}
            // A new board per filter, so a selection never carries over between Chats and Projects.
            key={showTools ? 'projects' : 'chats'}
            loading={showTools ? toolsLoading : (loading || !activeIdsLoaded)}
            movingId={movingId}
            onBulkDelete={handleKanbanBulkDelete}
            onCardClick={setStepsSession}
            onMove={moveTo}
            onStop={handleStopSession}
            parseSessionId={parseVfsSessionId}
            sessions={kanbanSessions}
          />
          </>
        ) : loading ? (
          <div className="memories-loading">
            <div className="memories-spinner" />
            <span>Loading conversations…</span>
          </div>
        ) : displayed.length === 0 ? (
          <div className="memories-empty">
            <Icon name="clock" size={40} />
            <p>
              {searchQuery.trim()
                ? 'No matching conversations'
                : (!showArchived && archivedCount > 0)
                  ? 'All your conversations are archived'
                  : 'No conversations yet'}
            </p>
            {!searchQuery.trim() && !showArchived && archivedCount > 0 ? (
              <button className="memories-select-btn" onClick={() => setShowArchived(true)} type="button">
                <Icon name="package" size={13} />
                {`Show archived (${archivedCount})`}
              </button>
            ) : !searchQuery.trim() && (
              <button className="memories-new-btn" onClick={handleNewChat} type="button">
                Start your first chat
              </button>
            )}
          </div>
        ) : (
          <div className="memories-list">
            {groups.map((group) => {
              const isCollapsed = collapsedMonths.has(group.key);
              return (
                <div className="memories-month-group" key={group.key}>
                  <button
                    className="memories-month-header"
                    onClick={() => toggleMonthCollapsed(group.key)}
                    type="button"
                  >
                    <Icon name={isCollapsed ? 'chevron-right' : 'chevron-down'} size={14} />
                    <span className="memories-month-label">{group.label}</span>
                    <span className="memories-month-count">{group.sessions.length}</span>
                  </button>
                  {!isCollapsed && group.sessions.map((session) => {
                    const parsed = parseVfsSessionId(session.id);
                    const isSelected = selectedIds.has(session.id);
                    const isPinned = Boolean(session.state?.pinned);
                    const isActive = activeIds.has(session.id);
                    const isArchived = Boolean(session.state?.archived);
                    return (
                      <div
                        key={session.id}
                        className={`memory-item${editingId === session.id ? ' editing' : ''}${deletingId === session.id ? ' deleting' : ''}${isSelected ? ' selected' : ''}${isPinned ? ' pinned' : ''}${isActive ? ' active-run' : ''}`}
                      >
                        {selectionMode && (
                          <label className="memory-item-checkbox" onClick={(e) => e.stopPropagation()}>
                            <input
                              checked={isSelected}
                              onChange={() => toggleSelected(session.id)}
                              type="checkbox"
                            />
                          </label>
                        )}
                        <button
                          className="memory-item-main"
                          disabled={editingId === session.id || deletingId === session.id}
                          onClick={() => selectionMode ? toggleSelected(session.id) : handleSelectConversation(session)}
                          type="button"
                        >
                          <div className="memory-item-info">
                            {editingId === session.id ? (
                              <input
                                autoFocus
                                className="memory-rename-input"
                                onChange={(e) => setEditingName(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={handleEditKeyDown}
                                type="text"
                                value={editingName}
                              />
                            ) : (
                              <div className="memory-name-row">
                                {isPinned && (
                                  <svg className="memory-pin-badge" fill="currentColor" height="12" stroke="none" viewBox="0 0 24 24" width="12">
                                    <path d="M12 2a1 1 0 011 1v6.5l4.4 3.52a1 1 0 01.37 1.13l-.16.4a1 1 0 01-.93.63H13v6a1 1 0 01-2 0v-6H7.32a1 1 0 01-.93-.63l-.16-.4a1 1 0 01.37-1.13L11 9.5V3a1 1 0 011-1z"/>
                                  </svg>
                                )}
                                <div className="memory-name">{session.name || 'Untitled Conversation'}</div>
                                {parsed?.projectId ? (
                                  <span className="memory-type-badge memory-type-badge--code">Code</span>
                                ) : (
                                  <span className="memory-type-badge memory-type-badge--chat">Chat</span>
                                )}
                                {isActive && (
                                  <span className="memory-wip-badge" title="This conversation is currently running">
                                    <span className="memory-wip-dot" />
                                    Running
                                  </span>
                                )}
                                {isArchived && (
                                  <span className="memory-type-badge memory-type-badge--archived" title="Archived">
                                    <Icon name="package" size={10} />
                                    Archived
                                  </span>
                                )}
                              </div>
                            )}
                            {editingId !== session.id && parsed?.serviceName && (
                              <div className="memory-project">{parsed.serviceName}</div>
                            )}
                          </div>
                          <div className="memory-date" title={new Date(session.lastUpdateTime * 1000).toLocaleString()}>
                            {formatDate(session.lastUpdateTime)}
                          </div>
                        </button>

                        <div className="memory-actions">
                          {editingId === session.id ? (
                            <>
                              <button className="mem-action-btn mem-action-confirm" disabled={savingId === session.id} onClick={handleEditConfirm} title="Confirm" type="button">
                                <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                              </button>
                              <button className="mem-action-btn mem-action-cancel" onClick={handleEditCancel} title="Cancel" type="button">
                                <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                                  <line x1="18" x2="6" y1="6" y2="18"/>
                                  <line x1="6" x2="18" y1="6" y2="18"/>
                                </svg>
                              </button>
                            </>
                          ) : deletingId === session.id ? (
                            <>
                              <button className="mem-action-btn mem-action-confirm mem-action-delete-confirm" onClick={(e) => handleDeleteConfirm(e, session.id)} title="Confirm delete" type="button">
                                <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                              </button>
                              <button className="mem-action-btn mem-action-cancel" onClick={handleDeleteCancel} title="Cancel" type="button">
                                <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                                  <line x1="18" x2="6" y1="6" y2="18"/>
                                  <line x1="6" x2="18" y1="6" y2="18"/>
                                </svg>
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className={`mem-action-btn mem-action-pin${isPinned ? ' active' : ''}`}
                                onClick={(e) => handleTogglePin(e, session)}
                                title={isPinned ? 'Unpin' : 'Pin'}
                                type="button"
                              >
                                <svg fill={isPinned ? 'currentColor' : 'none'} height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                                  <path d="M12 2v8l4.5 3.6a1 1 0 01-.6 1.8H8.1a1 1 0 01-.6-1.8L12 10"/>
                                  <path d="M12 15v7"/>
                                </svg>
                              </button>
                              <button className={`mem-action-btn mem-action-archive${isArchived ? ' active' : ''}`} onClick={(e) => handleArchive(e, session)} title={isArchived ? 'Unarchive' : 'Archive'} type="button">
                                <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                                  <rect height="5" rx="1" width="20" x="2" y="4"/>
                                  <path d="M4 9v9a2 2 0 002 2h12a2 2 0 002-2V9"/>
                                  <path d="M10 13h4"/>
                                </svg>
                              </button>
                              <button className="mem-action-btn mem-action-edit" onClick={(e) => handleEditClick(e, session)} title="Rename" type="button">
                                <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                              </button>
                              <button className="mem-action-btn mem-action-delete" onClick={(e) => handleDeleteClick(e, session.id)} title="Delete" type="button">
                                <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                                  <path d="M10 11v6"/>
                                  <path d="M14 11v6"/>
                                  <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={bulkDeleteConfirmOpen}
        message={`Delete ${selectedIds.size} conversation${selectedIds.size === 1 ? '' : 's'}? This can't be undone.`}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={handleBulkDeleteConfirm}
        title="Delete conversations"
      />

      <ConfirmDialog
        isOpen={Boolean(kanbanDeleteIds)}
        message={`Delete ${kanbanDeleteIds?.length || 0} conversation${(kanbanDeleteIds?.length || 0) === 1 ? '' : 's'}? This can't be undone.`}
        onCancel={() => setKanbanDeleteIds(null)}
        onConfirm={handleKanbanBulkDeleteConfirm}
        title="Delete conversations"
      />

      <ConfirmDialog
        isOpen={Boolean(projectDelete)}
        message={projectDelete ? `Delete ${plural(projectDelete.owned.length, 'project')}? This can't be undone.${projectDelete.notOwned.length > 0
          ? ` ${plural(projectDelete.notOwned.length, 'selected project')} you don't own will be skipped (${projectDelete.notOwned.join(', ')}): only the owner can delete a project.`
          : ''}` : ''}
        onCancel={() => setProjectDelete(null)}
        onConfirm={handleProjectDeleteConfirm}
        title={projectDelete ? `Delete ${plural(projectDelete.owned.length, 'project')}` : 'Delete projects'}
      />

      <SessionStepsDrawer
        onClose={() => setStepsSession(null)}
        onOpenSession={handleSelectConversation}
        session={stepsSession}
        userId={userId}
      />
    </div>
  );
}

const LABEL_BACKFILL_SKIP_KEY = 'mia_label_backfill_skip';

/** Sessions the label backfill must not ask about again (the backend answered 422 or 404 for them). */
function labelBackfillSkipped() {
  try {
    const ids = JSON.parse(localStorage.getItem(LABEL_BACKFILL_SKIP_KEY) || '[]');
    return new Set(Array.isArray(ids) ? ids : []);
  } catch {
    return new Set();
  }
}

function skipLabelBackfill(sessionId) {
  try {
    const ids = [...labelBackfillSkipped(), sessionId].slice(-500);
    localStorage.setItem(LABEL_BACKFILL_SKIP_KEY, JSON.stringify([...new Set(ids)]));
  } catch { /* storage unavailable: asked again next visit, once */ }
}

export default Memories;
